/* eslint-disable @typescript-eslint/no-unused-vars */

import { abbrToState } from "shared/app/VerificationTemplate/slices/CountyWalkInTypeSlice/constants";
import { EnrollmentOption, EnrollmentSource } from "types/landlord/enrollment";
import { AlgoliaUser } from "views/renters/types";

export type EnrollmentDisplayItem = { id: string; label: string };

const normalizeAddress = (s: string): string =>
  s.trim().toLowerCase().replace(/\s+/g, " ");

export const verboseEnrollmentType = (
  enrollmentType: string,
  lowercase: boolean = false
): string => {
  let verboseEnrollmentType = enrollmentType;
  if (enrollmentType === "direct") verboseEnrollmentType = "Required";
  if (enrollmentType === "opt_in") verboseEnrollmentType = "Opt-in";
  if (enrollmentType === "opt_out") verboseEnrollmentType = "Opt-out";
  if (enrollmentType === "default") verboseEnrollmentType = "Default";

  if (lowercase) return verboseEnrollmentType.toLowerCase();

  return verboseEnrollmentType;
};

export const prepareAlgoliaData = (
  data: AlgoliaUser[],
  enrollmentOption: EnrollmentOption
) => {
  return data.map((renter) => {
    const enrollmentSource: EnrollmentSource = "algolia";
    const { units, manager, phone, email, ...property } = renter.property;
    const { boomCustomerId, pets, hasPets, ...customer } = renter.customer;
    const {
      _highlightResult,
      boomEnrollmentStatus,
      property: _property,
      customer: _customer,
      ...renterDetails
    } = renter;

    const ret = {
      ...renterDetails,
      property,
      customer: customer,
      opt_out: enrollmentOption.optOut,
      opt_in: enrollmentOption.optIn,
      is_default_enrollment: enrollmentOption.isDefaultEnrollment,
      from_source: enrollmentSource
    };

    return ret;
  });
};

type OverrideEntry = {
  default_enrollment_type: string;
  property_name: string | null;
  address1: string;
};

const buildOverridesByAddress = (
  propertyDefaults: Array<{
    property_id: string;
    default_enrollment_type: string;
    property_name: string | null;
    address1?: string | null;
  }>
): Map<string, OverrideEntry> => {
  const map = new Map<string, OverrideEntry>();
  for (const p of propertyDefaults)
    if (p.address1) {
      const key = normalizeAddress(p.address1);
      map.set(key, {
        default_enrollment_type: p.default_enrollment_type,
        property_name: p.property_name,
        address1: p.address1
      });
    }
  return map;
};

/**
 * Calculates the count of renters by address or state from an array of renters.
 * Match by address1 first: if the renter's property address1 matches a property_defaults
 * entry, use that enrollment. Otherwise fall back to state-based defaults.
 */
export const groupedDefaultEnrollmentTypes = ({
  rentersToProcess,
  partnerDefaultEnrollmentType,
  defaultEnrollmentTypes,
  propertyDefaults = []
}: {
  rentersToProcess: AlgoliaUser[];
  partnerDefaultEnrollmentType: string | undefined;
  defaultEnrollmentTypes: Array<{
    state_province: string;
    default_enrollment_type: string;
  }>;
  propertyDefaults?: Array<{
    property_id: string;
    default_enrollment_type: string;
    property_name: string | null;
    address1?: string | null;
  }>;
}): EnrollmentDisplayItem[] => {
  const overridesByAddress = buildOverridesByAddress(propertyDefaults);
  const hasPartnerOrStateDefaults =
    !!partnerDefaultEnrollmentType || defaultEnrollmentTypes.length > 0;
  const hasPropertyDefaults = overridesByAddress.size > 0;
  if (!hasPartnerOrStateDefaults && !hasPropertyDefaults) return [];

  const stateCounts: Record<string, number> = {};
  const addressCounts: Record<string, number> = {};
  const addressMeta: Record<
    string,
    { name: string; default_enrollment_type: string }
  > = {};
  const statesWithDefaultEnrollmentType = new Set<string>();
  const optValueByState: Record<string, string> = {};

  defaultEnrollmentTypes.forEach((type) => {
    statesWithDefaultEnrollmentType.add(type.state_province);
    optValueByState[type.state_province] = type.default_enrollment_type;
  });

  const rentersWithoutOverride: AlgoliaUser[] = [];

  rentersToProcess.forEach((renter) => {
    const addr = renter.property.address1.trim();
    const key = addr ? normalizeAddress(addr) : "";
    const override = key ? overridesByAddress.get(key) : undefined;
    if (override) {
      addressCounts[key] = (addressCounts[key] || 0) + 1;
      addressMeta[key] = {
        name:
          override.property_name || renter.property.name || override.address1,
        default_enrollment_type: override.default_enrollment_type
      };
    } else rentersWithoutOverride.push(renter);
  });

  const result: EnrollmentDisplayItem[] = [];

  if (Object.keys(addressCounts).length > 0)
    for (const [key, count] of Object.entries(addressCounts)) {
      const { name, default_enrollment_type } = addressMeta[key];
      result.push({
        id: `address-${key}`,
        label: `${name}: ${count} ${verboseEnrollmentType(default_enrollment_type, true)}`
      });
    }

  if (rentersWithoutOverride.length === 0) return result;
  if (!partnerDefaultEnrollmentType) return result;

  rentersWithoutOverride.forEach((renter) => {
    const state = renter.property.state;
    if (statesWithDefaultEnrollmentType.has(state))
      stateCounts[state] = (stateCounts[state] || 0) + 1;
    else stateCounts["Other"] = (stateCounts["Other"] || 0) + 1;
  });

  const entries = Object.entries(stateCounts);
  const isSingleUnspecifiedState =
    entries.length === 1 && entries[0][0] === "Other";
  if (isSingleUnspecifiedState && !partnerDefaultEnrollmentType) return result;
  if (isSingleUnspecifiedState && partnerDefaultEnrollmentType) {
    result.push({
      id: "state-Other",
      label: `${stateCounts["Other"]} ${verboseEnrollmentType(partnerDefaultEnrollmentType)}`
    });
    return result;
  }

  for (const [state, count] of entries) {
    const id = `state-${state}`;
    const label =
      state === "Other"
        ? `Other: ${count} ${verboseEnrollmentType(partnerDefaultEnrollmentType || "", true)}`
        : `${abbrToState[state]}: ${count} ${verboseEnrollmentType(optValueByState[state] || "", true)}`;
    result.push({ id, label });
  }
  return result;
};

/**
 * Determines the enrollment type for a single renter. Match by address1 first:
 * if the property's address1 matches a property_defaults entry, use that enrollment;
 * otherwise fall back to state-based defaults.
 */
export const getRenterEnrollmentType = ({
  state,
  address1,
  propertyName,
  defaultEnrollmentTypes,
  partnerDefaultEnrollmentType,
  propertyDefaults = []
}: {
  state: string;
  address1?: string | null;
  propertyName?: string;
  defaultEnrollmentTypes: Array<{
    state_province: string;
    default_enrollment_type: string;
  }>;
  partnerDefaultEnrollmentType: string | undefined;
  propertyDefaults?: Array<{
    property_id: string;
    default_enrollment_type: string;
    property_name: string | null;
    address1?: string | null;
  }>;
}): EnrollmentDisplayItem[] => {
  const key = address1?.trim() ? normalizeAddress(address1) : "";
  const propertyOverride = key
    ? propertyDefaults.find(
        (p) => p.address1 && normalizeAddress(p.address1) === key
      )
    : undefined;
  if (propertyOverride) {
    const name =
      propertyOverride.property_name ||
      propertyName ||
      propertyOverride.address1 ||
      "Property";
    return [
      {
        id: `address-${key}`,
        label: `${name}: ${verboseEnrollmentType(propertyOverride.default_enrollment_type)}`
      }
    ];
  }
  if (!partnerDefaultEnrollmentType) return [];

  const stateEnrollmentType = defaultEnrollmentTypes.find(
    (type) => type.state_province === state
  );

  if (stateEnrollmentType)
    return [
      {
        id: `state-${state}`,
        label: `${abbrToState[state]}: ${verboseEnrollmentType(stateEnrollmentType.default_enrollment_type)}`
      }
    ];

  if (partnerDefaultEnrollmentType)
    return [
      {
        id: "partner-default",
        label: verboseEnrollmentType(partnerDefaultEnrollmentType)
      }
    ];

  return [];
};

export const getEnrollmentsByStateCopy = (
  enrollmentsByState: Record<string, number>,
  partnerDefaultEnrollmentType: string | undefined,
  defaultEnrollmentTypes: Array<{
    state_province: string;
    default_enrollment_type: string;
  }>
) => {
  if (!partnerDefaultEnrollmentType) return "";
  if (Object.keys(enrollmentsByState).length === 0) return "";

  const statesWithDefaultEnrollmentType = new Set<string>();
  const optValueByState: Record<string, string> = {};

  defaultEnrollmentTypes.forEach((type) => {
    statesWithDefaultEnrollmentType.add(type.state_province);
    optValueByState[type.state_province] = type.default_enrollment_type;
  });

  const entries = Object.entries(enrollmentsByState);
  const totalUnscopedCount = entries.reduce(
    (acc, [state, count]) =>
      statesWithDefaultEnrollmentType.has(state) ? acc : acc + count,
    0
  );

  const isSingleUnspecifiedState =
    entries.length === 1 && !statesWithDefaultEnrollmentType.has(entries[0][0]);
  if (isSingleUnspecifiedState && !partnerDefaultEnrollmentType) return "";

  if (isSingleUnspecifiedState && partnerDefaultEnrollmentType)
    return `${totalUnscopedCount} ${verboseEnrollmentType(partnerDefaultEnrollmentType)}`;

  const filteredEntries = entries.filter(([state, count]) =>
    statesWithDefaultEnrollmentType.has(state)
  );
  const mappedEntries = filteredEntries.map(
    ([state, count]) =>
      `${abbrToState[state]}: ${count} ${verboseEnrollmentType(optValueByState[state] || "", true)}`
  );
  if (totalUnscopedCount > 0 && partnerDefaultEnrollmentType)
    mappedEntries.push(
      `Other: ${totalUnscopedCount} ${verboseEnrollmentType(partnerDefaultEnrollmentType || "", true)}`
    );

  return mappedEntries.join(", ");
};
